<template>
  <div class="page-container dict-page">
    <!-- 左侧字典类型 -->
    <div class="left-type-panel">
      <div class="panel-header">
        <span class="panel-title">字典类型</span>
        <el-button type="primary" size="mini" icon="el-icon-plus" :disabled="typeLoading" @click="handleAddType">新增类型</el-button>
      </div>
      <div class="type-filters">
        <el-input
          v-model="sidebarQuery.keyword"
          class="filter-keyword"
          placeholder="名称 / 类型标识"
          clearable
          size="small"
          :disabled="typeLoading"
          @keyup.enter.native="handleSidebarQuery"
        />
        <el-select
          v-model="sidebarQuery.status"
          placeholder="状态"
          clearable
          size="small"
          class="filter-status"
          :disabled="typeLoading"
        >
          <el-option label="正常" value="0" />
          <el-option label="停用" value="1" />
        </el-select>
        <el-button type="primary" size="mini" icon="el-icon-search" :disabled="typeLoading" @click="handleSidebarQuery">搜索</el-button>
        <el-button size="mini" icon="el-icon-refresh" :disabled="typeLoading" @click="handleSidebarReset">重置</el-button>
      </div>
      <div class="type-list" v-loading="typeLoading">
        <div
          v-for="item in typeList"
          :key="item.dictId"
          class="type-item"
          :class="{ active: currentType && currentType.dictId === item.dictId }"
          @click="handleTypeClick(item)"
        >
          <div class="type-item-info">
            <div class="type-item-icon">{{ getTypeIcon(item.dictName) }}</div>
            <div class="type-item-text">
              <div class="type-item-name">{{ item.dictName }}</div>
              <div class="type-item-remark" :title="item.remark || ''">{{ item.remark || '' }}</div>
            </div>
          </div>
          <el-tag :type="item.status === '0' ? 'success' : 'danger'" size="mini" effect="plain">
            {{ item.status === '0' ? '正常' : '停用' }}
          </el-tag>
        </div>
        <div v-if="!typeLoading && typeList.length === 0" class="empty-placeholder">
          <i class="el-icon-folder-opened"></i>
          <p>暂无字典类型</p>
        </div>
      </div>
      <div v-if="typeTotal > sidebarQuery.pageSize" class="sidebar-pagination">
        <el-pagination
          small
          layout="prev, pager, next"
          :current-page="sidebarQuery.pageNo"
          :page-size="sidebarQuery.pageSize"
          :total="typeTotal"
          @current-change="handleSidebarPageChange"
        />
      </div>
    </div>

    <!-- 右侧字典数据 -->
    <div class="right-data-panel">
      <div class="panel-header">
        <span class="panel-title">
          字典数据列表
          <span v-if="currentType" style="color: #2296f7;"> - {{ currentType.dictName }}</span>
        </span>
        <div class="header-actions" v-if="currentType">
          <el-button type="primary" plain size="mini" icon="el-icon-edit" :disabled="typeLoading" @click="handleEditType">编辑类型</el-button>
        </div>
      </div>

      <div class="data-content">
        <!-- 字典数据列表 -->
        <DictDataPanel
          v-if="currentType"
          ref="dataPanel"
          :key="currentType.dictId"
          :dict-type="currentType.dictType"
        />
        <div v-else class="empty-placeholder center">
          <i class="el-icon-mouse"></i>
          <p>请在左侧选择字典类型</p>
        </div>
      </div>
    </div>

    <!-- 新增/编辑字典类型弹窗 -->
    <DictTypeDialog ref="typeDialog" @saved="handleTypeSaved" />
  </div>
</template>

<script>
// 服务
import { dictService } from './dict.service';
import { clearDictCache } from '@/features/components/dict/dict.api';
// 组件
import DictTypeDialog from './dictTypeDialog.vue';
import DictDataPanel from './dictDataPanel.vue';

export default {
  name: 'DictPage',
  components: { DictTypeDialog, DictDataPanel },

  data() {
    return {
      typeList: [],
      typeTotal: 0,
      typeLoading: false,
      sidebarQuery: {
        pageNo: 1,
        pageSize: 15,
        keyword: '',
        status: ''
      },
      currentType: null,
      /** 防止 mounted / watch 并发执行「按路由选中字典类型」 */
      _dictRouteApplyLock: false
    };
  },

  mounted() {
    const routeKey = this.$route.query.dictType;
    if (routeKey) {
      this.sidebarQuery.keyword = String(routeKey);
      this.sidebarQuery.pageNo = 1;
      this.sidebarQuery.status = '';
    }
    this.loadTypeList().then(() => this.trySelectFromRouteQuery());
  },

  watch: {
    '$route'(to) {
      if (to.name !== 'dictList' || !to.query || !to.query.dictType) return;
      const key = String(to.query.dictType);
      this.$nextTick(() => this.applyDictTypeFromQuery(key));
    }
  },

  methods: {
    getTypeIcon(name) {
      if (!name) return '📖';
      const s = String(name).trim();
      const ch = s[0];
      if (/[\u4e00-\u9fa5]/.test(ch)) return ch;
      return '📖';
    },

    loadTypeList() {
      this.typeLoading = true;
      return dictService
        .queryTypePage({ ...this.sidebarQuery })
        .then((res) => {
          this.typeList = res.data.rows || [];
          this.typeTotal = res.data.total || 0;
        })
        .finally(() => {
          this.typeLoading = false;
        });
    },

    handleSidebarQuery() {
      this.sidebarQuery.pageNo = 1;
      this.loadTypeList();
    },

    handleSidebarReset() {
      this.sidebarQuery = {
        pageNo: 1,
        pageSize: 15,
        keyword: '',
        status: ''
      };
      this.loadTypeList();
    },

    // 分页切换
    handleSidebarPageChange(page) {
      this.sidebarQuery.pageNo = page;
      this.loadTypeList();
    },

    // 点击字典类型
    handleTypeClick(item) {
      this.currentType = item;
    },

    /** 从路由 query.dictType 选中左侧类型（业务弹窗「字段管理」跳转时带入） */
    trySelectFromRouteQuery() {
      const key = this.$route.query.dictType;
      if (!key) return Promise.resolve();
      return this.applyDictTypeFromQuery(String(key));
    },

    applyDictTypeFromQuery(dictTypeKey) {
      if (!dictTypeKey || this.$route.name !== 'dictList') return Promise.resolve();
      if (this._dictRouteApplyLock) return Promise.resolve();
      this._dictRouteApplyLock = true;

      const releaseLock = () => {
        this._dictRouteApplyLock = false;
      };

      const clearQuery = () => {
        if (this.$route.query.dictType) {
          this.$router.replace({ name: 'dictList', query: {} }).catch(() => {});
        }
      };
      const pick = () =>
        this.typeList.find((t) => String(t.dictType || '').trim() === String(dictTypeKey).trim());
      let found = pick();
      if (found) {
        this.handleTypeClick(found);
        clearQuery();
        releaseLock();
        return Promise.resolve();
      }
      const prevSidebar = { ...this.sidebarQuery };
      this.sidebarQuery.keyword = dictTypeKey;
      this.sidebarQuery.pageNo = 1;
      this.sidebarQuery.status = '';
      return this.loadTypeList().then(() => {
        found = pick();
        if (found) {
          this.handleTypeClick(found);
        } else {
          this.$message.warning('未找到该字典类型，请确认已在「字典类型」中创建对应类型标识');
          this.sidebarQuery = { ...prevSidebar };
          return this.loadTypeList();
        }
      }).finally(() => {
        clearQuery();
        releaseLock();
      });
    },

    // 新增字典类型
    handleAddType() {
      this.$refs.typeDialog.open(null);
    },
    // 编辑字典类型
    handleEditType() {
      if (!this.currentType) return;
      this.$refs.typeDialog.open(this.currentType);
    },

    handleTypeSaved() {
      clearDictCache();
      this.loadTypeList().then(() => {
        if (this.currentType) {
          const id = this.currentType.dictId;
          const found = this.typeList.find((t) => t.dictId === id);
          if (found) this.currentType = found;
        }
        this.$nextTick(() => {
          if (this.$refs.dataPanel) this.$refs.dataPanel.refresh();
        });
      });
    }
  }
};
</script>

<style scoped lang="scss">
.dict-page {
  display: flex;
  height: calc(100vh - 125px);
  background: #f5f7fa;
  padding: 15px;
  gap: 15px;
  box-sizing: border-box;

  .left-type-panel {
    width: 400px;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    min-height: 0;

    .panel-header {
      padding: 15px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;

      .panel-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }
    }

    .type-filters {
      padding: 10px 15px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
      flex-wrap: nowrap;

      .filter-keyword {
        flex: 1;
        min-width: 0;
      }

      .filter-status {
        width: 92px;
        flex-shrink: 0;
      }
    }

    .type-list {
      flex: 1;
      overflow: auto;
      padding: 10px;
      min-height: 0;

      .type-item {
        padding: 12px 15px;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        background: #f5f7fa;
        border: 1px solid #e4e7ed;
        transition: all 0.2s;

        &:hover {
          border-color: #2296f7;
        }

        &.active {
          background: #ecf5ff;
          border-color: #2296f7;
        }

        .type-item-info {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;

          .type-item-icon {
            width: 36px;
            height: 36px;
            border-radius: 8px;
            background: #ecf5ff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            flex-shrink: 0;
          }

          .type-item-text {
            min-width: 0;
          }

          .type-item-name {
            font-size: 14px;
            color: #303133;
            font-weight: 500;
          }

          .type-item-remark {
            font-size: 12px;
            color: #909399;
            margin-top: 2px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }

    .sidebar-pagination {
      padding: 8px 12px 12px;
      border-top: 1px solid #ebeef5;
      display: flex;
      justify-content: center;
      flex-shrink: 0;
    }
  }

  .right-data-panel {
    flex: 1;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    min-height: 0;

    .panel-header {
      padding: 15px 20px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;

      .panel-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }

      .header-actions {
        display: flex;
        gap: 10px;
      }
    }

    .data-content {
      flex: 1;
      padding: 20px;
      overflow: auto;
      min-height: 0;
    }
  }

  .empty-placeholder {
    padding: 40px 0;
    text-align: center;
    color: #909399;

    &.center {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      min-height: 200px;
    }

    i {
      font-size: 48px;
      margin-bottom: 10px;
      color: #e4e7ed;
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }
}
</style>

<style lang="scss">
.el-message {
  z-index: 10000 !important;
}
.el-message-box__wrapper {
  z-index: 10001 !important;
}
</style>
